import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion, Popup, Icon, Grid, Menu, Transition } from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import _ from 'lodash'
import icons from '../Icons'
import './Sidebar.scss'
import styles from './../../styles/global.module.scss'
import { useUniversalLateralMenuHook } from 'hooks/useUniversalLateralMenuHook'
import { isTabSwitchNavigation, useTabNavigation } from 'hooks/useTabNavigation'

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window
    return { width, height }
}
const isImageIcon = (v) =>
    typeof v === 'string' && (/^https?:\/\//.test(v) || v.startsWith('/') || v.startsWith('data:'))

const normalizeGroup = (s = '') =>
    (s ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

// Helpers para normalizar paths y detectar rutas activas
const stripQueryHash = (s = '') => String(s).split('#')[0].split('?')[0]
const normalizePath = (s = '/') => {
    const clean = stripQueryHash(s || '/').toLowerCase().trim()
    const withSlash = ('/' + clean).replace(/\/+/g, '/')
    return withSlash !== '/' ? withSlash.replace(/\/$/, '') : '/'
}
const stripBasePrefix = (pathname) => {
    const norm = normalizePath(pathname)
    const parts = norm.split('/')
    const baseSet = new Set(['dashboard', 'home', 'inicio'])
    return (parts.length > 2 && baseSet.has(parts[1])) ? '/' + parts.slice(2).join('/') : norm
}
const equalsOrParentOf = (current, target) =>
    current === target || current.startsWith(target + '/')

const AssociateSideBar = (props) => {
    const { t } = useTranslation()
    const { expand, setExpand, user, location, mobileOnly, setMobileOnly, lang, onTabOpen, onMenuOpen } = props

    const [previousExpand, setPreviousExpand] = useState(null)
    const { lateralMenu } = useUniversalLateralMenuHook('associate');

    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())
    const [openHeaderIndices, setOpenHeaderIndices] = useState([])
    const [openSubHeaderTitles, setOpenSubHeaderTitles] = useState([])

    const activeItemRef = useRef(null)
    const containerRef = useRef(null)
    const manualToggleForPath = useRef(null)
    const closeStickyRef = useRef(null)
    const [stickyOffset, setStickyOffset] = useState(80);
    const [companyLogoSrc, setCompanyLogoSrc] = useState(null)
    const [logoDimensions, setLogoDimensions] = useState({
        menu: null,
        default: null
    })

    const underlineMenu = false

    const getLogoFitScore = (naturalWidth, naturalHeight, frameWidth, frameHeight) => {
        if (!naturalWidth || !naturalHeight || !frameWidth || !frameHeight) return -1

        const containScale = Math.min(frameWidth / naturalWidth, frameHeight / naturalHeight)
        const renderedWidth = naturalWidth * containScale
        const renderedHeight = naturalHeight * containScale
        const widthRatio = renderedWidth / frameWidth
        const heightRatio = renderedHeight / frameHeight
        const usedRatio = Math.max(widthRatio, heightRatio)

        const verticalPenalty = heightRatio > 0.94 ? (heightRatio - 0.94) * 0.15 : 0
        const compactPenalty = widthRatio < 0.56 ? (0.56 - widthRatio) * 0.35 : 0
        return usedRatio - verticalPenalty - compactPenalty
    }

    const companyLogoFrameRef = useRef(null)

    const medirLogo = (tipo, img) => {
        if (!img?.naturalWidth || !companyLogoFrameRef?.current) return

        const frameEl = companyLogoFrameRef.current
        const frameWidth = frameEl.clientWidth || 0
        const frameHeight = frameEl.clientHeight || 0

        if (!frameWidth || !frameHeight) return

        setLogoDimensions(prev => ({
            ...prev,
            [tipo]: {
                width: img.naturalWidth,
                height: img.naturalHeight
            }
        }))
    }

    const marcarError = (tipo) => {
        setLogoDimensions(prev => ({
            ...prev,
            [tipo]: { width: 0, height: 0 }
        }))
    }

    // --- Mapa de iconos desde Icons.js ---
const groupIconMap = useMemo(() => {
        const map = new Map()
            ; (icons || []).forEach((i) => map.set(normalizeGroup(i.title), i.url))
        return map
    }, [])

    const { tabs: openTabs, activeTabId } = useTabNavigation();

    // Sincronizar lateralMenu con expand en TIEMPO REAL (fijado automático)
    useEffect(() => {
        if (mobileOnly || lateralMenu === null) return;
        if (lateralMenu === true) {
            setExpand(1);
        } else if (lateralMenu === false) {
            if (typeof onTabOpen !== 'function') setExpand(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lateralMenu]);

    useEffect(() => {
        const menuLogo = props.user?.sys_company_data?.menu_logo_url || null
        const defaultLogo = props.user?.sys_company_data?.logo_url || null

        if (!menuLogo && !defaultLogo) {
            setCompanyLogoSrc(null)
            return
        }

        if (!menuLogo) {
            setCompanyLogoSrc(defaultLogo)
            return
        }

        if (!defaultLogo) {
            setCompanyLogoSrc(menuLogo)
            return
        }

        if (!logoDimensions.menu || !logoDimensions.default) {
            setCompanyLogoSrc(menuLogo)
            return
        }

        const frameEl = companyLogoFrameRef?.current
        const frameWidth = frameEl?.clientWidth || 0
        const frameHeight = frameEl?.clientHeight || 0

        if (!frameWidth || !frameHeight) {
            setCompanyLogoSrc(menuLogo)
            return
        }

        const scoreMenu = getLogoFitScore(
            logoDimensions.menu.width,
            logoDimensions.menu.height,
            frameWidth,
            frameHeight
        )

        const scoreDefault = getLogoFitScore(
            logoDimensions.default.width,
            logoDimensions.default.height,
            frameWidth,
            frameHeight
        )

        setCompanyLogoSrc(scoreMenu >= scoreDefault ? menuLogo : defaultLogo)
    }, [
        props.user?.sys_company_data?.menu_logo_url,
        props.user?.sys_company_data?.logo_url,
        logoDimensions,
        expand,
    ])

    // --- Responsive: detectar mobile ---
    useEffect(() => {
        function handleResize() {
            const dims = getWindowDimensions()
            if (dims.width !== windowDimensions.width || dims.height !== windowDimensions.height) {
                setWindowDimensions(dims)
            }
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [windowDimensions])

    useEffect(() => {
        if (windowDimensions.width <= 425) {
            setMobileOnly(true)
            if (expand === 1 || expand == null) setExpand(1)
        } else {
            setMobileOnly(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowDimensions])

    useEffect(() => {
        if (!location) return
        if (mobileOnly) setExpand(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, mobileOnly])

    // Medir dinámicamente la altura del header sticky para compensar el offset
    useEffect(() => {
        const updateStickyOffset = () => {
            if (!closeStickyRef.current) return
            const nextOffset = closeStickyRef.current.offsetHeight || 0
            if (nextOffset > 0) setStickyOffset(nextOffset)
        }
        const id = window.requestAnimationFrame(updateStickyOffset)
        window.addEventListener('resize', updateStickyOffset)
        return () => {
            window.cancelAnimationFrame(id)
            window.removeEventListener('resize', updateStickyOffset)
        }
    }, [expand, windowDimensions.width])

    // --- Preparar módulos desde user.modules ---
    const modulesRaw = user?.modules || []

    const modulesToRender = useMemo(() => {
        return modulesRaw.filter((mod) => {
            const permisitions = mod.permisitions ?? mod.permissions
            return permisitions?.ACCESS ?? true
        })
    }, [modulesRaw])

    const headers = useMemo(() => {
        return modulesToRender.reduce((accu, module) => {
            const x = accu.find((item) => item.module_group == module.module_group)
            return x ? accu : accu.concat([module])
        }, [])
    }, [modulesToRender])

    const subHeaders = useMemo(() => {
        const arr = modulesToRender.reduce((accu, module) => {
            if (module.sub_group && !accu.find(item => item.sub_group === module.sub_group && item.group === module.module_group)) {
                accu.push({
                    title: module.sub_group,
                    group: module.module_group,
                    sub_group: module.sub_group,
                    ...module,
                })
            }
            return accu
        }, [])
        return _.uniqBy(arr, (ih) => `${ih.group}-${ih.sub_group}`)
    }, [modulesToRender])

    const routes = useMemo(() => {
        return modulesToRender.flatMap((m) => {
            if (m.isVisible) {
                return [{
                    ...m,
                    title: m.name,
                    to: m.url,
                    url: m.url,
                    group: m.module_group,
                    sub_group: m.sub_group,
                    icon: m.icon,
                }]
            }
            return []
        })
    }, [modulesToRender])

    const rawPath = (typeof location?.hash === 'string' && location.hash.startsWith('#'))
        ? location.hash.slice(1)
        : (location?.pathname || '/')
    const currentPath = normalizePath(rawPath)
    const appPath = stripBasePrefix(rawPath)
    const shouldPreserveSidebarState = isTabSwitchNavigation(location)

const isActiveRoute = useCallback((routeUrl) => {
        if (!routeUrl) return false
        const target = normalizePath(`/${routeUrl}`)
        return equalsOrParentOf(appPath, target) || equalsOrParentOf(currentPath, target)
    }, [currentPath, appPath])

    const normalizeTabPath = (path) => {
        const normalized = `/${path || ''}`.replace(/\/+/g, '/')
        return normalized === '/' ? '/' : normalized.replace(/\/$/, '')
    }
    const isRouteInOpenTab = (routePath) => {
        if (!openTabs || !Array.isArray(openTabs)) return false
        const normalized = normalizeTabPath(routePath)
        return openTabs.some(tab => normalizeTabPath(tab.path) === normalized)
    }
    const isRouteInActiveTab = (routePath) => {
        if (!openTabs || !activeTabId) return false
        const normalized = normalizeTabPath(routePath)
        const activeTab = openTabs.find(tab => tab.id === activeTabId)
        return activeTab && normalizeTabPath(activeTab.path) === normalized
    }

    const activeRoute = routes.find(r => isActiveRoute(r.url))
    const activeGroup = activeRoute?.group || null

    // --- Auto-open módulo activo ---
    useEffect(() => {
        if (!lateralMenu) return
        if (shouldPreserveSidebarState) return
        if (!routes?.length || !headers?.length) return
        if (manualToggleForPath.current === currentPath) {
            manualToggleForPath.current = null
            return
        }
        const activeRoute = routes.find(r => isActiveRoute(r.url))
        if (!activeRoute) return
        const hIndex = headers.findIndex(h => h.module_group === activeRoute.group)
        if (hIndex >= 0) {
            setOpenHeaderIndices(prev => (prev.includes(hIndex) ? prev : [...prev, hIndex]))
        }
        if (activeRoute.sub_group) {
            setOpenSubHeaderTitles(prev => (prev.includes(activeRoute.sub_group) ? prev : [...prev, activeRoute.sub_group]))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath, routes, headers, lateralMenu, isActiveRoute, shouldPreserveSidebarState])

    // --- Scroll al item activo ---
    useEffect(() => {
        if (shouldPreserveSidebarState) return
        const t = setTimeout(() => {
            const el = activeItemRef.current
            const container = containerRef.current
            if (!el || !container) return
            try {
                const elRect = el.getBoundingClientRect()
                const contRect = container.getBoundingClientRect()
                if (elRect.top < contRect.top || elRect.bottom > contRect.bottom) {
                    const offset = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2
                    container.scrollTo({ top: Math.max(0, offset), behavior: 'auto' })
                }
            } catch (e) {
                try { el.scrollIntoView({ block: 'nearest', behavior: 'auto' }) } catch { el.scrollIntoView() }
            }
        }, 100)
        return () => clearTimeout(t)
    }, [currentPath, expand, shouldPreserveSidebarState])

    const handleHeaderClick = useCallback((e, titleObj) => {
        e.preventDefault(); e.stopPropagation()
        const index = titleObj.index ?? titleObj
        manualToggleForPath.current = currentPath
        setOpenHeaderIndices((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]))
    }, [currentPath])

    const handleSubHeaderClick = useCallback((e, titleObj) => {
        e.preventDefault(); e.stopPropagation()
        const key = titleObj?.title ?? titleObj
        setOpenSubHeaderTitles((prev) => (prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]))
    }, [])

    const handleTabOpen = (e, route) => {
        if (typeof onTabOpen === 'function') {
            e.preventDefault()
            onTabOpen(route)
        }
    }

    const getHeaderDepthClass = (headerIndex) =>
        openHeaderIndices.includes(headerIndex) ? 'depth-parent' : ''

    const getSubHeaderDepthClass = (subHeaderTitle) =>
        openSubHeaderTitles.includes(subHeaderTitle) ? 'depth-parent' : ''

    const getRouteDepthClassForSubHeader = (subHeaderTitle) =>
        openSubHeaderTitles.includes(subHeaderTitle) ? 'depth-current' : ''

    const getRouteDepthClassForHeader = (headerIndex) =>
        openHeaderIndices.includes(headerIndex) ? 'depth-current' : ''

    return (
        <div
            className='sidebar'
            ref={containerRef}
            style={{
                width: expand == null || expand == 1 ? '225px' : '0px',
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            {/* Header Sticky: Burger + Logo */}
            {(expand == null || expand == 1) && (
                <div className='Sidebar__CloseStickyy' ref={closeStickyRef} style={{ padding: 0, margin: 0 }}>
                    <div className='Sidebar__Header'>
{/* Fila 1: Solo botón hamburguesa */}
                        <div className='Sidebar__MenuButtonRow' style={{justifyContent:'flex-start',paddingLeft:'0',paddingRight:'0'}}>
                            <button
                                className='Sidebar__MenuButton Sidebar__MenuButton--shell'
                                onClick={() => {
                                    if (lateralMenu && !mobileOnly) {
                                        return
                                    }

                                    setExpand((previousExpandedState) => {
                                        setPreviousExpand(previousExpandedState)
                                        const newExpandState = !mobileOnly
                                            ? previousExpandedState == 1
                                                ? (previousExpand == null || previousExpand == 2) ? 0 : 2
                                                : 1
                                            : !previousExpandedState ? 1 : 0

                                        return newExpandState
                                    })
                                }}
                                aria-label={t('Abrir/cerrar menú lateral')}
                                style={{background:'transparent',border:'1.1px solid #6c6c6c',borderRadius:7,padding:'0 13px 0 3px',height:26,minWidth:62,display:'flex',alignItems:'center',gap:3,boxShadow:'none',justifyContent:'flex-start',textAlign:'left'}}
                            >
                                <img
                                    src='https://img.icons8.com/?size=100&id=100033&format=png&color=000000'
                                    alt='Menu'
                                    style={{width:14.5,height:14.5,marginRight:-6,marginLeft:0,display:'inline-block'}}
                                />
                                <span style={{fontSize:13,color:'#6c6c6c',fontWeight:500,whiteSpace:'nowrap',marginLeft:0}}>ocultar</span>
                            </button>
                        </div>

                        {/* Fila 2: Logo */}
                        <div className='Sidebar__LogoRow'>
                            <div className='Sidebar__LogoSlot'>
                                <div className='Sidebar__LogoFrame' ref={companyLogoFrameRef}>
                                    {/* Logos ocultos para medir */}
                                    {props.user?.sys_company_data?.menu_logo_url && (
                                        <img
                                            src={props.user.sys_company_data.menu_logo_url}
                                            style={{ display: 'none' }}
                                            onLoad={(e) => medirLogo('menu', e.target)}
                                            onError={() => marcarError('menu')}
                                        />
                                    )}
                                    {props.user?.sys_company_data?.logo_url && (
                                        <img
                                            src={props.user.sys_company_data.logo_url}
                                            style={{ display: 'none' }}
                                            onLoad={(e) => medirLogo('default', e.target)}
                                            onError={() => marcarError('default')}
                                        />
                                    )}

                                    {/* Logo visible */}
                                    {companyLogoSrc ? (
                                        <img
                                            src={companyLogoSrc}
                                            alt='Logo'
                                            className='Sidebar__CompanyLogo SideBar__Outline'
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <div className='Sidebar__LogoPlaceholder' />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Transition visible={expand == null || expand == 1} animation='fade down' duration={260} unmountOnHide={false}>
                <div style={{ marginTop: stickyOffset }}>
                    {headers.map((header, index) => {
                        const isOpen = openHeaderIndices.includes(index)
                        return (
                            <Accordion key={`accordion-${header.module_group}-${index}`} fluid style={{ paddingLeft: 8, paddingBottom: 5, paddingTop: 5 }}>
                                <Accordion.Title
                                    active={isOpen}
                                    index={index}
                                    onClick={handleHeaderClick}
                                    className={getHeaderDepthClass(index)}
                                >
<Menu.Item
                                        className={`${styles.shrinkOnHover} SubMenu__Item ${underlineMenu ? 'lighten-hover-border' : ''}${activeGroup === header.module_group ? ' is-active' : ''}`}
                                        style={{ width: '100%', display: 'flex', alignItems: 'center' }}
                                    >
                                        <Icon
                                            name='dropdown'
                                            style={{
                                                marginRight: 6,
                                                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                opacity: isOpen ? 1 : 0,
                                                transition: 'opacity 220ms, transform 220ms',
                                                pointerEvents: 'none',
                                            }}
                                        />
                                        {(() => {
                                            const key = normalizeGroup(header.module_group)
                                            const iconValue = groupIconMap.get(key) ?? header.icon ?? 'archive'
                                            return isImageIcon(iconValue) ? (
                                                <img src={iconValue} alt='' style={{ width: 24, height: 24, marginRight: 8 }} />
                                            ) : (
                                                <Icon name={iconValue} size='large' style={{ marginRight: 8 }} />
                                            )
                                        })()}
                                        {(expand == null || expand == 1) && (
                                            <h5 className={`menu-row-title ${getHeaderDepthClass(index)}`} style={{ margin: 0 }}>
                                                {t(header.module_group)}
                                            </h5>
                                        )}
                                    </Menu.Item>
                                </Accordion.Title>

                                <Transition visible={isOpen} animation='fade down' duration={220} unmountOnHide={false}>
                                    <Accordion.Content active={isOpen} style={{ paddingTop: 0 }}>
                                        <Grid divided='vertically' style={{ width: '100%' }}>
                                            <Grid.Row style={{ padding: 0 }} />
                                            <Grid.Row style={{ padding: '0 0 0 15px' }}>
                                                <Grid.Column>
                                                    {/* SubHeaders */}
                                                    {subHeaders.map((subHeader, indexSH) => {
                                                        if (subHeader.group !== header.module_group) return null
                                                        const isSubOpen = openSubHeaderTitles.includes(subHeader.title)
                                                        return (
                                                            <Accordion key={`sub-${indexSH}`} fluid style={{ marginTop: 0 }}>
                                                                <Accordion.Title
                                                                    active={isSubOpen}
                                                                    onClick={(e) => handleSubHeaderClick(e, subHeader)}
                                                                    className={getSubHeaderDepthClass(subHeader.title)}
                                                                >
                                                                    <Menu.Item style={{ display: 'flex', alignItems: 'center' }}>
                                                                        <Icon
                                                                            name='dropdown'
                                                                            style={{
                                                                                transform: isSubOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                                opacity: isSubOpen ? 1 : 0,
                                                                                transition: 'opacity 220ms, transform 220ms',
                                                                            }}
                                                                        />
                                                                        <span style={{ marginLeft: 6 }}>{t(subHeader.title)}</span>
                                                                    </Menu.Item>
                                                                </Accordion.Title>
                                                                <Transition visible={isSubOpen} animation='fade down' duration={220}>
                                                                    <Accordion.Content active={isSubOpen} style={{ paddingTop: 0 }}>
                                                                        <Menu vertical fluid text style={{ paddingLeft: 30 }}>
                                                                            {routes.map((route, indexR) => {
                                                                                if (route.sub_group !== subHeader.title) return null
                                                                                const active = isActiveRoute(route.url)
                                                                                return (
                                                                                    <Link
                                                                                        key={`r-${indexR}`}
                                                                                        to={`/${route.to}`}
className={`sub_menu_row ${isRouteInActiveTab(route.url) ? 'is-active' : ''} ${isRouteInOpenTab(route.url) && !isRouteInActiveTab(route.url) ? 'has-tab-open' : ''}`}
                                                                                        onClick={(e) => handleTabOpen(e, route)}
                                                                                    >
                                                                                        <Menu.Item className={styles.growOnHover} style={{ margin: 0 }}>
                                                                                            <span
                                                                                                ref={active ? activeItemRef : null}
                                                                                                className={getRouteDepthClassForSubHeader(subHeader.title)}
                                                                                            >
                                                                                                {t(route.name)}
                                                                                            </span>
                                                                                        </Menu.Item>
                                                                                    </Link>
                                                                                )
                                                                            })}
                                                                        </Menu>
                                                                    </Accordion.Content>
                                                                </Transition>
                                                            </Accordion>
                                                        )
                                                    })}
                                                    {/* Rutas sin subgrupo */}
                                                    <Menu vertical fluid text style={{ paddingLeft: 18 }}>
                                                        {routes.map((route, indexR) => {
                                                            if (route.group !== header.module_group || route.sub_group) return null
                                                            const active = isActiveRoute(route.url)
                                                            return (
                                                                <Link
                                                                    key={`link-${indexR}`}
                                                                    to={`/${route.to}`}
className={`sub_menu_row ${isRouteInActiveTab(route.url) ? 'is-active' : ''} ${isRouteInOpenTab(route.url) && !isRouteInActiveTab(route.url) ? 'has-tab-open' : ''}`}
                                                                    onClick={(e) => handleTabOpen(e, route)}
                                                                >
                                                                    <Menu.Item className={styles.growOnHover} style={{ margin: 0 }}>
                                                                        <span
                                                                            ref={active ? activeItemRef : null}
                                                                            className={getRouteDepthClassForHeader(index)}
                                                                        >
                                                                            {t(route.name)}
                                                                        </span>
                                                                    </Menu.Item>
                                                                </Link>
                                                            )
                                                        })}
                                                    </Menu>
                                                </Grid.Column>
                                            </Grid.Row>
                                        </Grid>
                                    </Accordion.Content>
                                </Transition>
                            </Accordion>
                        )
                    })}
                </div>
            </Transition>
        </div>
    )
}

export default AssociateSideBar
